import { ReactElement, useMemo, useRef, useState } from 'react'
import {
  DrawControllerWrapper,
  TargetPlane,
  useTapelineCentersSnapTargets,
  useTapelineSnapTargets,
} from '@scene'
import { equalizeClosePolygonEdgesHeight } from '@structuralPlanningUtils'
import { some } from 'lodash-es'
import { Plane, Vector2 } from 'three'
import { useTheme } from '@mui/material'
import {
  DraggableRectangleXYRef,
  DraggableRectangleXY,
  DrawControllerRef,
  TransientDrawState,
  getRectCornersFromStartAndEndPoint,
  ImmutableVector3,
  guidFromDrawControllerEvent,
} from '@modugen/ui-toolkit'
import { DEFAULT_NUM_DECIMALS_ROUNDING_DRAWER_2D } from '@ui/forms/CoordinatePointInput/constants'
import { useControlStore } from '@editorStores'
import { useClickActionEnabled } from '@editorHooks'
import { useStructuralPlanningDrawerEsc } from '@structuralPlanningHooks'
import getPlaneLineIntersection from 'src/components/pages/Editor/utils/getPlaneLineIntersection'
import { roundImmutableVector3, roundXYImmutableVector3 } from 'src/geometry/rounding'
import { prepareRoofShapeFromPoints } from '../../../RectangularShape/components/RectangularShapeDrawer/utils'
import { useModelSnapTargets } from '../../hooks'

const targetName = 'plane-target'

interface Props {
  /**
   * All model elements that the user is allowed to draw to (e.g. slabs/roofs).
   * Once drawing interaction starts the target element is used to create a
   * plane to draw to
   */
  targetElements: Record<string, ImmutableVector3[]>

  minShapeSize?: number
  /**
   * called after rectangle has been drawn. Will be provided the target guid of
   * the target defined in targetElements
   * @param points
   * @returns
   */
  roundNumDecimalsXY?: number
  onRectangleDrawn: (points: ImmutableVector3[], targetGuid: string) => void
}

const RectangularShapeDrawer2D = ({
  targetElements,
  minShapeSize = 0.1,
  roundNumDecimalsXY,

  onRectangleDrawn,
}: Props): ReactElement => {
  const { scenePalette } = useTheme()

  const drawControllerRef = useRef<DrawControllerRef>(null)
  const rectangleRef = useRef<DraggableRectangleXYRef>(null)

  const [activeTarget, setActiveTarget] = useState<string | null>(null)

  // SNAPPING RELATED

  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)

  const tapelineSnapTargets = useTapelineSnapTargets()
  const tapelineCenterTargets = useTapelineCentersSnapTargets()
  const snapTargets = useModelSnapTargets({ xyOnly: true })

  // EVENTS

  const onDrawStart = (transientDrawState: TransientDrawState) => {
    const { drawPoint, drawTarget } = transientDrawState

    if (drawPoint && drawTarget && rectangleRef.current) {
      const guid = guidFromDrawControllerEvent(transientDrawState)

      setActiveTarget(guid || null)

      rectangleRef.current.setHandleAndStartDragging('End', drawPoint)
    }
  }

  const onDrawMouseMove = (transientDrawState: TransientDrawState) => {
    const { drawPoint, drawTarget } = transientDrawState

    if (drawPoint && drawTarget && rectangleRef.current) {
      rectangleRef.current.updateActiveHandle(drawPoint)
    }
  }

  const onDrawEnd = () => {
    if (rectangleRef.current && activeTarget) {
      const [drawnStart, drawnEnd] = rectangleRef.current.stopDraggingAndGetPoints()
      const roundedDefaultStart = roundImmutableVector3(
        drawnStart,
        DEFAULT_NUM_DECIMALS_ROUNDING_DRAWER_2D,
      )
      const roundedDefaultEnd = roundImmutableVector3(
        drawnEnd,
        DEFAULT_NUM_DECIMALS_ROUNDING_DRAWER_2D,
      )
      const roundedStart = roundNumDecimalsXY
        ? roundXYImmutableVector3(roundedDefaultStart, roundNumDecimalsXY)
        : roundedDefaultStart
      const roundedEnd = roundNumDecimalsXY
        ? roundXYImmutableVector3(roundedDefaultEnd, roundNumDecimalsXY)
        : roundedDefaultEnd

      const drawnPoints = getRectCornersFromStartAndEndPoint(roundedStart, roundedEnd)

      // As we did draw in 2d we need to project the points to the original roof slab

      // this is the original slab/roof we have been drawing on
      const originalTarget = targetElements[activeTarget]

      const originalPlane = new Plane().setFromCoplanarPoints(
        originalTarget[0].v,
        originalTarget[1].v,
        originalTarget[2].v,
      )

      const projectedPoints = drawnPoints.map(
        p => getPlaneLineIntersection(originalPlane, new Vector2(p.x, p.y)) as ImmutableVector3,
      )

      // even after projecting the points there is a really really slight
      // missmatch of z. Hence we equalize it here if there are similar heights.
      const pointsEqualized = equalizeClosePolygonEdgesHeight(projectedPoints)

      if (drawnStart.distanceTo(drawnEnd) >= minShapeSize) {
        const sortedPoints = prepareRoofShapeFromPoints(pointsEqualized)
        onRectangleDrawn(sortedPoints, activeTarget)
      }

      setActiveTarget(null)
    }
  }

  useStructuralPlanningDrawerEsc(() => {
    drawControllerRef.current?.abortDrawing()

    // not so nice way to reset the draggable rectangle to it's defaults
    // we really need a better DraggableRectangle implementation similar to InteractiveLine

    const defaultPoint = new ImmutableVector3(0, 0, -1000)
    rectangleRef.current?.setHandleAndStartDragging('Start', defaultPoint)
    rectangleRef.current?.stopDraggingAndGetPoints()
    rectangleRef.current?.setHandleAndStartDragging('End', defaultPoint)
    rectangleRef.current?.stopDraggingAndGetPoints()

    setActiveTarget(null)
  }, !!activeTarget)

  const drawControllerEnabled = useClickActionEnabled()

  const drawTargetGuids = useMemo(() => {
    return new Set([...Object.keys(targetElements), targetName])
  }, [targetElements])

  return (
    <>
      <DrawControllerWrapper
        xyOnly
        indicatorType="crosshair"
        ref={drawControllerRef}
        enabled={drawControllerEnabled}
        enableIndicator
        snapToCornersAndEdges={snapToCornersAndEdges}
        orthoSnap={snapOrthogonal}
        snapToAngles={false}
        onDrawStart={onDrawStart}
        onMouseMove={onDrawMouseMove}
        onDrawEnd={onDrawEnd}
        isValidDrawTarget={object => {
          const isValid = drawTargetGuids.has(object.name)
          if (isValid) return true

          if ('instanceIdToId' in object.userData) {
            const ids = Object.values(object.userData.instanceIdToId) as string[]

            const validTarget = some(ids, id => drawTargetGuids.has(id))

            return validTarget
          }

          return false
        }}
        additionalSnapTargets={[...snapTargets, ...tapelineSnapTargets, ...tapelineCenterTargets]}
      />

      <DraggableRectangleXY
        ref={rectangleRef}
        // define rectangle far out of user sight in order to prevent flickering
        // of length indicator at 0,0,0
        points={[new ImmutableVector3(-100, -100, -100), new ImmutableVector3(-100, -100, -100)]}
        color={scenePalette.elements3d.vertical_slabs as string}
        showIndicators={false}
        rectangleProps={{
          opacity: 1,
        }}
      />

      {activeTarget && <TargetPlane points={targetElements[activeTarget]} name={targetName} />}
    </>
  )
}

export default RectangularShapeDrawer2D
